import { useAuthStore } from '../store/authStore';
import axios from 'axios';

const API_URL = '/v2/api';

export interface FilterCondition {
  field: string;
  operator: '==' | '!=' | '<' | '<=' | '>' | '>=' | 'in' | 'array-contains';
  value: unknown;
}

export type FilterOrGroup = FilterCondition | FilterCondition[];

export interface OrderBy {
  field: string;
  direction: 'asc' | 'desc';
}

export interface QueryRequest {
  collection?: string;
  filters: FilterOrGroup[];
  orderBy?: OrderBy[];
  limit?: number;
  cursor?: string; // Document ID for cursor-based pagination
  select?: string[];
  includeChangeHistory?: boolean;
}

export interface QueryResponse {
  success: boolean;
  data: Array<Record<string, unknown>>;
  changeHistory?: unknown[];
  metadata: {
    count: number;
    hasMore: boolean;
    cursor?: string; // Next page cursor
    queryTime: string;
  };
  error?: string;
  details?: string;
}

export interface SavedQuery {
  id: string;
  name: string;
  description?: string;
  query: QueryRequest[];
  userId: string;
  operatorId?: string;
  createdAt: string; // ISO string
  updatedAt: string; // ISO string
  lastUsedAt?: string; // ISO string
  usageCount: number;
  isPublic?: boolean;
}

export interface PaginatedSavedQueriesResponse {
  success: boolean;
  data: SavedQuery[];
  metadata: {
    page: number;
    limit: number;
    total: number;
    hasMore: boolean;
  };
}

export interface SaveQueryRequest {
  name: string;
  description?: string;
  query: QueryRequest[];
}

const getAuthHeaders = (): Record<string, string> => {
  const { token } = useAuthStore.getState();
  if (token) {
    return { Authorization: `Bearer ${token}` };
  }

  try {
    const storageData = localStorage.getItem('auth-storage');
    if (storageData) {
      const parsed = JSON.parse(storageData as string);
      const persistedToken = parsed?.state?.token;
      if (persistedToken) {
        return { Authorization: `Bearer ${persistedToken}` };
      }
    }
  } catch (error) {
    console.error('Failed to read auth token from storage:', error);
  }

  return {};
};

export const queryApi = {
  execute: async (queries: QueryRequest[]): Promise<QueryResponse[]> => {
    const response = await axios.post(`${API_URL}/query`, queries, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  getSavedQueries: async (
    page = 1,
    limit = 20,
  ): Promise<PaginatedSavedQueriesResponse> => {
    const response = await axios.get(`${API_URL}/query/saved`, {
      headers: getAuthHeaders(),
      params: { page, limit },
    });
    return response.data;
  },

  saveQuery: async (savedQuery: SaveQueryRequest): Promise<SavedQuery> => {
    const response = await axios.post(`${API_URL}/query/saved`, savedQuery, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  updateSavedQuery: async (
    id: string,
    savedQuery: SaveQueryRequest,
  ): Promise<SavedQuery> => {
    const response = await axios.put(
      `${API_URL}/query/saved/${id}`,
      savedQuery,
      {
        headers: getAuthHeaders(),
      },
    );
    return response.data;
  },

  deleteSavedQuery: async (id: string): Promise<void> => {
    await axios.delete(`${API_URL}/query/saved/${id}`, {
      headers: getAuthHeaders(),
    });
  },

  executeSavedQuery: async (id: string): Promise<QueryResponse[]> => {
    const response = await axios.post(
      `${API_URL}/query/saved/${id}/execute`,
      undefined,
      {
        headers: getAuthHeaders(),
      },
    );
    return response.data;
  },

  getCollections: async (): Promise<string[]> => {
    const response = await axios.get(`${API_URL}/query/collections`, {
      headers: getAuthHeaders(),
    });
    return response.data.collections;
  },
};
